/**
 * SECTOR SEVEN — seventh cartridge, second vector cartridge (MVP).
 *
 * Heritage: Tempest (1981) — defender on the rim of a polygonal tube,
 * enemies climb out from the vanishing point along radial lanes — plus
 * Gyruss (1983) for the rotational shooter framing. Atari color-vector
 * aesthetic: black void, neon-green tube wireframe, magenta enemies.
 *
 * Original mechanic: **dive into the tube**. Pressing fireB plunges
 * the player ~40% into the tube for 1 second; during the dive, lane
 * rotation is locked but bullets deal 3× damage. Risk/reward — commit
 * to a lane, can't reposition, but you can punch through a Pulsar or
 * a Spiker stack you'd otherwise have to dodge.
 *
 * Coordinate model (see tube.ts):
 *   - depth = 0  → on the rim, where the player sits
 *   - depth = 1  → at the vanishing point (canvas center)
 *   - bullets travel +depth (away from camera into tube)
 *   - enemies travel -depth (climbing toward camera)
 *
 * MVP cuts:
 *   - hex (odd levels) + octagon (even levels) only; star + cross
 *     deferred to followup
 *   - Spiker spikes don't yet physically block bullets (they're still
 *     destructible obstacles that take lane real estate)
 *   - Pulsar pulse renders as a flash, not an animated bloom
 *   - Dive is a discrete state (full descent), not an animated lerp
 *
 * Determinism: all randomness routes through ctx.random (enemy spawn
 * lane + timing, Flipper flip direction, Pulsar pulse phase offset).
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  BULLET,
  BULLET_POWER,
  BULLET_POWER_TRAIL,
  BULLET_TRAIL,
  DIVE_FLASH,
  FIELD_BG,
  FLASH_HIT,
  FLIPPER_BLOOM,
  FLIPPER_LINE,
  HUD_ALERT,
  HUD_DIVE,
  HUD_LABEL,
  HUD_LEVEL,
  HUD_LIVES,
  HUD_SCORE,
  PLAYER_BLOOM,
  PLAYER_DIVE_BLOOM,
  PLAYER_DIVE_LINE,
  PLAYER_LINE,
  PULSAR_BLOOM,
  PULSAR_LINE,
  PULSAR_PULSE_BLOOM,
  PULSAR_PULSE_LINE,
  RIM_BLOOM,
  RIM_LINE,
  SPIKE,
  SPIKE_BLOOM,
  SPIKER_BLOOM,
  SPIKER_LINE,
  TUBE_BLOOM,
  TUBE_LINE,
} from './palette.js';
import {
  CANVAS_H,
  CANVAS_W,
  laneAngle,
  laneCenter,
  laneCorners,
  laneCountForLevel,
  rimVertex,
} from './tube.js';

// --- Player -----------------------------------------------------------
const PLAYER_ROT_COOLDOWN = 8; // ticks between lane snaps
const PLAYER_FIRE_COOLDOWN = 10; // 6 shots / sec
const PLAYER_DIVE_TICKS = 60; // 1.0s @ 60Hz
const PLAYER_DIVE_DEPTH = 0.4; // how far into the tube the dive sits
const PLAYER_DIVE_COOLDOWN = 120; // 2.0s between dives
const DIVE_DAMAGE_MULT = 3;

// --- Bullets ----------------------------------------------------------
const BULLET_MAX = 8;
const BULLET_VD = 0.025; // depth/tick → 40 ticks rim-to-VP

// --- Enemies ----------------------------------------------------------
type EnemyKind = 'flipper' | 'spiker' | 'pulsar';

const FLIPPER_VD = -0.0045; // climbs out in ~220 ticks
const FLIPPER_HP = 1;
const FLIPPER_SCORE = 100;
const FLIPPER_FLIP_TICKS = 18; // animation length of an edge flip
const FLIPPER_RIM_REVERSE_VD = 0.002; // after flip, briefly descends again

const SPIKER_VD = -0.0035;
const SPIKER_HP = 2;
const SPIKER_SCORE = 200;
const SPIKER_LAY_INTERVAL = 60; // lay a spike every 60 ticks

const PULSAR_VD = -0.0042;
const PULSAR_HP = 1;
const PULSAR_SCORE = 300;
const PULSAR_PULSE_INTERVAL = 90; // ticks between pulses
const PULSAR_PULSE_DURATION = 30; // ticks of invuln+rim-kill

// --- Spikes -----------------------------------------------------------
const SPIKE_HP = 1;
const SPIKE_SCORE = 50;
/** Spike grows from a depth slice; SPIKE_LENGTH is the depth-span the
 *  spike occupies (so it visually reads as a streak). */
const SPIKE_LENGTH = 0.12;

// --- Spawning ---------------------------------------------------------
const SPAWN_BASE_INTERVAL = 110; // ticks
const SPAWN_MIN_INTERVAL = 38;
const SPAWN_INTERVAL_LEVEL_DROP = 8;
const ENEMIES_PER_LEVEL_BASE = 14;
const ENEMIES_PER_LEVEL_GROWTH = 4;

// --- Lives ------------------------------------------------------------
const LIVES_START = 3;
const RESPAWN_INVULN = 60;
const CRASH_FLASH_TICKS = 22;

// --- Render constants -------------------------------------------------
const TUBE_DEPTH_RINGS = [0.25, 0.5, 0.75]; // perspective rings for depth cues

type Phase = 'playing' | 'crashed' | 'level-clear' | 'game-over';

interface Bullet {
  lane: number;
  depth: number;
  /** True if fired during a dive — gets the 3× damage multiplier + a
   *  different visual. */
  powered: boolean;
  life: number;
}

interface Enemy {
  kind: EnemyKind;
  lane: number;
  depth: number;
  hp: number;
  /** Flipper: tick at which the edge-flip animation began, or -1. */
  flipStarted: number;
  /** Flipper: target lane after the flip lands. */
  flipTargetLane: number;
  /** Flipper: brief descent after a rim arrival. */
  rimReverseTicks: number;
  /** Spiker: tick of last spike deposit. */
  lastSpikeTick: number;
  /** Pulsar: tick offset for the pulse cycle (so they're not all in
   *  sync). */
  pulseOffset: number;
}

interface Spike {
  lane: number;
  depth: number; // top end of the spike (closer to rim)
  hp: number;
}

export class SectorSevenInstance implements GameInstance {
  private readonly ctx: GameContext;

  private tickCount = 0;
  private phase: Phase = 'playing';
  private crashFlash = 0;
  private levelClearTicks = 0;

  private score = 0;
  private lives = LIVES_START;
  private level = 1;
  private finalScore = 0;
  private finalLevel = 1;

  private laneCount: number;
  private playerLane = 0;
  private playerDiving = false;
  private diveTicksRemaining = 0;
  private diveCooldown = 0;
  private rotCooldown = 0;
  private fireCooldown = 0;
  private invuln = RESPAWN_INVULN;

  private bullets: Bullet[] = [];
  private enemies: Enemy[] = [];
  private spikes: Spike[] = [];

  private enemiesKilledThisLevel = 0;
  private enemiesNeededThisLevel: number;
  private enemiesSpawnedThisLevel = 0;
  private spawnTimer = 0;

  private inputLeft = false;
  private inputRight = false;
  private firePressed = false;
  private divePressed = false;
  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.laneCount = laneCountForLevel(this.level);
    this.enemiesNeededThisLevel =
      ENEMIES_PER_LEVEL_BASE + (this.level - 1) * ENEMIES_PER_LEVEL_GROWTH;
    this.spawnTimer = this.spawnIntervalForLevel();
    this.playerLane = 0;
  }

  // -- GameInstance contract -------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inputLeft = state.left;
    this.inputRight = state.right;
    this.firePressed =
      state.justPressed.has('fireA') || state.justPressed.has('start');
    // Dive is edge-triggered on fireB so holding doesn't repeat.
    this.divePressed = state.justPressed.has('fireB');
  }

  update(_dt: number): void {
    this.tickCount += 1;

    if (this.phase === 'game-over') return;

    if (this.phase === 'crashed') {
      this.crashFlash -= 1;
      if (this.crashFlash <= 0) {
        if (this.lives > 0) {
          this.respawn();
        } else {
          this.finalScore = this.score;
          this.finalLevel = this.level;
          this.phase = 'game-over';
        }
      }
      this.advanceBullets();
      this.advanceEnemies(); // keep field moving during the flash
      return;
    }

    if (this.phase === 'level-clear') {
      this.levelClearTicks -= 1;
      if (this.levelClearTicks <= 0) {
        this.startLevel(this.level + 1);
      }
      return;
    }

    if (this.invuln > 0) this.invuln -= 1;
    if (this.rotCooldown > 0) this.rotCooldown -= 1;
    if (this.fireCooldown > 0) this.fireCooldown -= 1;
    if (this.diveCooldown > 0) this.diveCooldown -= 1;

    this.handlePlayerRotate();
    this.handlePlayerDive();
    this.handlePlayerFire();

    this.advanceBullets();
    this.advanceEnemies();
    this.handleEnemySpawns();
    this.checkCollisions();

    if (
      this.enemiesKilledThisLevel >= this.enemiesNeededThisLevel &&
      this.enemies.length === 0 &&
      this.spikes.length === 0
    ) {
      this.score += 500 * this.level;
      this.phase = 'level-clear';
      this.levelClearTicks = 90;
      this.ctx.audio.sfx('level-up');
    }
  }

  render(g: CanvasRenderingContext2D): void {
    g.fillStyle = FIELD_BG;
    g.fillRect(0, 0, CANVAS_W, CANVAS_H);

    this.renderTube(g);
    this.renderSpikes(g);
    this.renderEnemies(g);
    this.renderBullets(g);
    this.renderPlayer(g);

    if (this.playerDiving) {
      g.fillStyle = DIVE_FLASH;
      g.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }
    if (this.phase === 'crashed' && this.crashFlash > CRASH_FLASH_TICKS - 6) {
      g.fillStyle = FLASH_HIT;
      g.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }

    this.renderHUD(g);

    if (this.phase === 'level-clear') this.renderLevelBanner(g);
    if (this.phase === 'game-over') this.renderGameOver(g);
  }

  resize(): void {}
  pause(): void {}
  resume(): void {}
  teardown(): void {
    this.bullets = [];
    this.enemies = [];
    this.spikes = [];
  }

  getScore(): number {
    return Math.floor(this.phase === 'game-over' ? this.finalScore : this.score);
  }
  getFinalScore(): number {
    return Math.floor(this.phase === 'game-over' ? this.finalScore : this.score);
  }
  isGameOver(): boolean {
    return this.phase === 'game-over';
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // -- Level transitions -----------------------------------------------

  private startLevel(level: number): void {
    this.level = level;
    this.laneCount = laneCountForLevel(level);
    this.enemiesNeededThisLevel =
      ENEMIES_PER_LEVEL_BASE + (level - 1) * ENEMIES_PER_LEVEL_GROWTH;
    this.enemiesKilledThisLevel = 0;
    this.enemiesSpawnedThisLevel = 0;
    this.spawnTimer = this.spawnIntervalForLevel();
    this.bullets = [];
    this.enemies = [];
    this.spikes = [];
    this.playerLane = 0;
    this.playerDiving = false;
    this.diveTicksRemaining = 0;
    this.diveCooldown = 0;
    this.invuln = RESPAWN_INVULN;
    this.phase = 'playing';
  }

  private respawn(): void {
    this.bullets = [];
    this.invuln = RESPAWN_INVULN;
    this.playerDiving = false;
    this.diveTicksRemaining = 0;
    this.phase = 'playing';
  }

  private spawnIntervalForLevel(): number {
    return Math.max(
      SPAWN_MIN_INTERVAL,
      SPAWN_BASE_INTERVAL - (this.level - 1) * SPAWN_INTERVAL_LEVEL_DROP
    );
  }

  // -- Player ----------------------------------------------------------

  private handlePlayerRotate(): void {
    if (this.playerDiving) return; // locked during dive
    if (this.rotCooldown > 0) return;
    if (this.inputLeft && !this.inputRight) {
      this.playerLane = (this.playerLane - 1 + this.laneCount) % this.laneCount;
      this.rotCooldown = PLAYER_ROT_COOLDOWN;
      this.ctx.audio.sfx('rotate');
    } else if (this.inputRight && !this.inputLeft) {
      this.playerLane = (this.playerLane + 1) % this.laneCount;
      this.rotCooldown = PLAYER_ROT_COOLDOWN;
      this.ctx.audio.sfx('rotate');
    }
  }

  private handlePlayerDive(): void {
    if (this.playerDiving) {
      this.diveTicksRemaining -= 1;
      if (this.diveTicksRemaining <= 0) {
        this.playerDiving = false;
      }
      return;
    }
    if (this.divePressed && this.diveCooldown === 0) {
      this.playerDiving = true;
      this.diveTicksRemaining = PLAYER_DIVE_TICKS;
      this.diveCooldown = PLAYER_DIVE_COOLDOWN;
      this.divePressed = false;
      this.ctx.audio.sfx('dive');
    }
  }

  private handlePlayerFire(): void {
    if (!this.firePressed) return;
    this.firePressed = false;
    if (this.fireCooldown > 0) return;
    if (this.bullets.length >= BULLET_MAX) return;
    this.bullets.push({
      lane: this.playerLane,
      depth: this.playerDiving ? PLAYER_DIVE_DEPTH : 0,
      powered: this.playerDiving,
      life: 80,
    });
    this.fireCooldown = PLAYER_FIRE_COOLDOWN;
    this.ctx.audio.sfx('shoot');
  }

  // -- Bullets ---------------------------------------------------------

  private advanceBullets(): void {
    for (const b of this.bullets) {
      b.depth += BULLET_VD;
      b.life -= 1;
    }
    this.bullets = this.bullets.filter(
      (b) => b.depth < 0.98 && b.life > 0
    );
  }

  // -- Enemies + spikes -----------------------------------------------

  private advanceEnemies(): void {
    for (const e of this.enemies) {
      if (e.kind === 'flipper') {
        if (e.flipStarted >= 0) {
          const ticks = this.tickCount - e.flipStarted;
          if (ticks >= FLIPPER_FLIP_TICKS) {
            // Flip completes: switch lane, brief reverse-descent.
            e.lane = e.flipTargetLane;
            e.flipStarted = -1;
            e.rimReverseTicks = 50;
          }
        } else if (e.rimReverseTicks > 0) {
          e.rimReverseTicks -= 1;
          e.depth = Math.min(0.6, e.depth + FLIPPER_RIM_REVERSE_VD);
        } else {
          e.depth += FLIPPER_VD;
          if (e.depth <= 0.02) {
            // Reached the rim — start a flip to an adjacent lane.
            e.flipStarted = this.tickCount;
            e.flipTargetLane =
              (e.lane +
                (this.ctx.random.next() < 0.5 ? 1 : -1) +
                this.laneCount) %
              this.laneCount;
            e.depth = 0;
          }
        }
      } else if (e.kind === 'spiker') {
        e.depth += SPIKER_VD;
        if (this.tickCount - e.lastSpikeTick >= SPIKER_LAY_INTERVAL) {
          e.lastSpikeTick = this.tickCount;
          this.spikes.push({
            lane: e.lane,
            depth: Math.max(0.05, e.depth - 0.05),
            hp: SPIKE_HP,
          });
          this.ctx.audio.sfx('spike-laid');
        }
      } else if (e.kind === 'pulsar') {
        e.depth += PULSAR_VD;
        // Pulse cycle: every PULSAR_PULSE_INTERVAL ticks, enter
        // PULSAR_PULSE_DURATION ticks of pulse.
        const t = (this.tickCount + e.pulseOffset) % PULSAR_PULSE_INTERVAL;
        if (t === 0) {
          this.ctx.audio.sfx('pulsar-pulse');
        }
      }
    }
    // Cull enemies that climbed past the rim — but only non-flipper
    // (flippers transition into flip animation at depth=0 instead).
    this.enemies = this.enemies.filter((e) => {
      if (e.depth < -0.05) return false;
      return e.hp > 0;
    });
    // Cull dead spikes.
    this.spikes = this.spikes.filter((s) => s.hp > 0);
  }

  private isPulsarPulsing(e: Enemy): boolean {
    if (e.kind !== 'pulsar') return false;
    const t = (this.tickCount + e.pulseOffset) % PULSAR_PULSE_INTERVAL;
    return t < PULSAR_PULSE_DURATION;
  }

  private handleEnemySpawns(): void {
    if (this.enemiesSpawnedThisLevel >= this.enemiesNeededThisLevel) return;
    this.spawnTimer -= 1;
    if (this.spawnTimer > 0) return;
    this.spawnTimer = this.spawnIntervalForLevel();

    // Pick a kind weighted by level — early levels mostly Flipper,
    // later levels mix in Spiker + Pulsar.
    const r = this.ctx.random.next();
    let kind: EnemyKind;
    if (this.level === 1) {
      kind = r < 0.85 ? 'flipper' : 'spiker';
    } else if (this.level === 2) {
      kind = r < 0.6 ? 'flipper' : r < 0.85 ? 'spiker' : 'pulsar';
    } else {
      kind = r < 0.4 ? 'flipper' : r < 0.7 ? 'spiker' : 'pulsar';
    }

    const lane = Math.floor(this.ctx.random.next() * this.laneCount);
    this.enemies.push({
      kind,
      lane,
      depth: 0.95,
      hp: enemyHp(kind),
      flipStarted: -1,
      flipTargetLane: lane,
      rimReverseTicks: 0,
      lastSpikeTick: this.tickCount,
      pulseOffset: Math.floor(this.ctx.random.next() * PULSAR_PULSE_INTERVAL),
    });
    this.enemiesSpawnedThisLevel += 1;
  }

  // -- Collisions ------------------------------------------------------

  private checkCollisions(): void {
    // Bullets vs enemies + spikes. Same-lane match + depth proximity.
    for (const b of this.bullets) {
      if (b.life <= 0) continue;
      // Check enemies first (so a bullet that hits an enemy on the way
      // past a spike still pays out the bigger reward).
      let hit = false;
      for (const e of this.enemies) {
        // Mid-flip flippers are between lanes — unhittable until the
        // flip completes and they snap onto the target lane.
        if (e.flipStarted >= 0) continue;
        if (e.lane !== b.lane) continue;
        if (Math.abs(b.depth - e.depth) > 0.04) continue;
        if (this.isPulsarPulsing(e)) continue;
        e.hp -= this.playerDiving ? DIVE_DAMAGE_MULT : 1;
        b.life = 0;
        if (e.hp <= 0) {
          this.score += enemyScore(e.kind);
          this.enemiesKilledThisLevel += 1;
          this.ctx.audio.sfx('enemy-kill');
        }
        hit = true;
        break;
      }
      if (hit) continue;
      for (const s of this.spikes) {
        if (s.lane !== b.lane) continue;
        if (b.depth < s.depth - SPIKE_LENGTH) continue;
        if (b.depth > s.depth + 0.04) continue;
        s.hp -= 1;
        b.life = 0;
        if (s.hp <= 0) {
          this.score += SPIKE_SCORE;
        }
        break;
      }
    }

    // Death checks (only if not invuln).
    if (this.invuln > 0) return;

    // Enemy on player's lane reaching the rim.
    for (const e of this.enemies) {
      if (e.lane !== this.playerLane) continue;
      if (e.depth <= 0.04 && e.flipStarted < 0 && e.rimReverseTicks === 0) {
        this.kill();
        return;
      }
    }
    // Pulsar pulsing while player shares its lane.
    for (const e of this.enemies) {
      if (e.kind !== 'pulsar') continue;
      if (e.lane !== this.playerLane) continue;
      if (this.isPulsarPulsing(e) && e.depth < 0.5) {
        this.kill();
        return;
      }
    }
    // Spike at the rim on the player's lane.
    for (const s of this.spikes) {
      if (s.lane !== this.playerLane) continue;
      if (s.depth <= 0.05) {
        this.kill();
        return;
      }
    }
  }

  private kill(): void {
    this.lives -= 1;
    this.phase = 'crashed';
    this.crashFlash = CRASH_FLASH_TICKS;
    this.playerDiving = false;
    this.diveTicksRemaining = 0;
    this.ctx.audio.sfx('crash');
  }

  // -- Rendering -------------------------------------------------------

  private renderTube(g: CanvasRenderingContext2D): void {
    // Lane radials — VP to each rim vertex.
    for (let i = 0; i < this.laneCount; i++) {
      const v = rimVertex(this.laneCount, i);
      strokeLineBloom(g, CANVAS_W / 2, CANVAS_H / 2, v.x, v.y, TUBE_LINE, TUBE_BLOOM);
    }
    // Inner depth-cue rings — polygons interpolated at a few depths.
    for (const d of TUBE_DEPTH_RINGS) {
      for (let i = 0; i < this.laneCount; i++) {
        const a = laneCorners(this.laneCount, i, d).left;
        const b = laneCorners(this.laneCount, (i + 1) % this.laneCount, d).left;
        strokeLineBloom(g, a.x, a.y, b.x, b.y, TUBE_LINE, TUBE_BLOOM);
      }
    }
    // Outer rim — bright accent so the player can read where the
    // playing edge is at a glance.
    for (let i = 0; i < this.laneCount; i++) {
      const a = rimVertex(this.laneCount, i);
      const b = rimVertex(this.laneCount, (i + 1) % this.laneCount);
      strokeLineBloom(g, a.x, a.y, b.x, b.y, RIM_LINE, RIM_BLOOM);
    }
  }

  private renderSpikes(g: CanvasRenderingContext2D): void {
    for (const s of this.spikes) {
      const top = laneCenter(this.laneCount, s.lane, Math.max(0, s.depth - SPIKE_LENGTH));
      const bot = laneCenter(this.laneCount, s.lane, s.depth);
      strokeLineBloom(g, top.x, top.y, bot.x, bot.y, SPIKE, SPIKE_BLOOM);
    }
  }

  private renderEnemies(g: CanvasRenderingContext2D): void {
    for (const e of this.enemies) {
      const pos = laneCenter(this.laneCount, e.lane, e.depth);
      const scale = 0.4 + (1 - e.depth) * 0.6; // 0.4 near VP, 1.0 at rim
      const baseSize = 22 * scale;
      const angle = laneAngle(this.laneCount, e.lane);

      if (e.kind === 'flipper') {
        // Flipper: a chevron pointing toward the rim.
        const flipping = e.flipStarted >= 0;
        let drawAngle = angle;
        if (flipping) {
          const t = (this.tickCount - e.flipStarted) / FLIPPER_FLIP_TICKS;
          // Spin around the rim midpoint between current + target lane.
          drawAngle += t * Math.PI;
        }
        strokeChevron(g, pos.x, pos.y, drawAngle, baseSize, FLIPPER_LINE, FLIPPER_BLOOM);
      } else if (e.kind === 'spiker') {
        // Spiker: a barbed diamond.
        strokeDiamond(g, pos.x, pos.y, angle, baseSize, SPIKER_LINE, SPIKER_BLOOM);
      } else {
        // Pulsar: a ring; pulses white.
        const pulsing = this.isPulsarPulsing(e);
        const line = pulsing ? PULSAR_PULSE_LINE : PULSAR_LINE;
        const bloom = pulsing ? PULSAR_PULSE_BLOOM : PULSAR_BLOOM;
        strokeRing(g, pos.x, pos.y, baseSize, line, bloom);
      }
    }
  }

  private renderBullets(g: CanvasRenderingContext2D): void {
    for (const b of this.bullets) {
      const pos = laneCenter(this.laneCount, b.lane, b.depth);
      const trail = laneCenter(
        this.laneCount,
        b.lane,
        Math.max(0, b.depth - 0.03)
      );
      g.lineCap = 'round';
      g.lineWidth = 5;
      g.strokeStyle = b.powered ? BULLET_POWER_TRAIL : BULLET_TRAIL;
      g.beginPath();
      g.moveTo(trail.x, trail.y);
      g.lineTo(pos.x, pos.y);
      g.stroke();
      g.lineWidth = 2.5;
      g.strokeStyle = b.powered ? BULLET_POWER : BULLET;
      g.beginPath();
      g.moveTo(trail.x, trail.y);
      g.lineTo(pos.x, pos.y);
      g.stroke();
      g.lineCap = 'butt';
    }
  }

  private renderPlayer(g: CanvasRenderingContext2D): void {
    if (this.invuln > 0 && this.invuln % 8 < 4) return; // blink
    const depth = this.playerDiving ? PLAYER_DIVE_DEPTH : 0;
    const pos = laneCenter(this.laneCount, this.playerLane, depth);
    const angle = laneAngle(this.laneCount, this.playerLane);
    const scale = 1 - depth * 0.5;
    const line = this.playerDiving ? PLAYER_DIVE_LINE : PLAYER_LINE;
    const bloom = this.playerDiving ? PLAYER_DIVE_BLOOM : PLAYER_BLOOM;
    // Player is a "claw" pointing into the tube (toward VP).
    strokeClaw(g, pos.x, pos.y, angle, 26 * scale, line, bloom);
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.textAlign = 'left';
    g.textBaseline = 'top';
    g.font = '12px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('SCORE', 16, 12);
    g.font = 'bold 18px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 16, 30);

    g.textAlign = 'right';
    g.font = '12px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('LEVEL', CANVAS_W - 16, 12);
    g.font = 'bold 18px ui-monospace, monospace';
    g.fillStyle = HUD_LEVEL;
    g.fillText(String(this.level).padStart(2, '0'), CANVAS_W - 16, 30);

    // Lives icons.
    g.fillStyle = HUD_LIVES;
    for (let i = 0; i < this.lives; i++) {
      g.fillRect(CANVAS_W - 16 - i * 14 - 8, 56, 8, 3);
    }

    // Dive cooldown bar — left side.
    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('DIVE', 16, CANVAS_H - 30);
    const diveReady = this.diveCooldown === 0 && !this.playerDiving;
    const diveFrac = this.playerDiving
      ? this.diveTicksRemaining / PLAYER_DIVE_TICKS
      : 1 - this.diveCooldown / PLAYER_DIVE_COOLDOWN;
    g.fillStyle = diveReady ? HUD_DIVE : HUD_LABEL;
    g.fillRect(16, CANVAS_H - 16, 100 * diveFrac, 5);
    g.strokeStyle = HUD_LABEL;
    g.lineWidth = 1;
    g.strokeRect(16 + 0.5, CANVAS_H - 16 + 0.5, 99, 4);

    // Enemy progress bar — center bottom.
    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.textAlign = 'center';
    g.fillText(
      `WAVE  ${this.enemiesKilledThisLevel} / ${this.enemiesNeededThisLevel}`,
      CANVAS_W / 2,
      CANVAS_H - 24
    );
    g.textAlign = 'left';
  }

  private renderLevelBanner(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0, 0, 0, 0.55)';
    g.fillRect(0, 0, CANVAS_W, CANVAS_H);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 36px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`SECTOR ${this.level} CLEAR`, CANVAS_W / 2, CANVAS_H / 2 - 14);
    g.font = '18px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText(
      `+${500 * this.level} CLEAR BONUS`,
      CANVAS_W / 2,
      CANVAS_H / 2 + 18
    );
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0, 0, 0, 0.72)';
    g.fillRect(0, 0, CANVAS_W, CANVAS_H);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 40px ui-monospace, monospace';
    g.fillStyle = HUD_ALERT;
    g.fillText('GAME OVER', CANVAS_W / 2, CANVAS_H / 2 - 36);
    g.font = '20px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(
      `SCORE  ${String(Math.floor(this.finalScore)).padStart(6, '0')}`,
      CANVAS_W / 2,
      CANVAS_H / 2 + 8
    );
    g.fillStyle = HUD_LEVEL;
    g.fillText(`SECTOR  ${this.finalLevel}`, CANVAS_W / 2, CANVAS_H / 2 + 36);
    g.font = '16px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    if (Math.floor(this.tickCount / 30) % 2 === 0) {
      g.fillText('PRESS Z TO RESTART', CANVAS_W / 2, CANVAS_H / 2 + 70);
    }
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }
}

// --- Helpers ----------------------------------------------------------

function enemyHp(kind: EnemyKind): number {
  if (kind === 'flipper') return FLIPPER_HP;
  if (kind === 'spiker') return SPIKER_HP;
  return PULSAR_HP;
}

function enemyScore(kind: EnemyKind): number {
  if (kind === 'flipper') return FLIPPER_SCORE;
  if (kind === 'spiker') return SPIKER_SCORE;
  return PULSAR_SCORE;
}

/** Two-pass bloom line — wide alpha underlay + bright thin overlay.
 *  Same trick Astrofall uses to fake CRT phosphor halo. */
function strokeLineBloom(
  g: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  bright: string,
  bloom: string
): void {
  g.lineWidth = 3;
  g.strokeStyle = bloom;
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.stroke();
  g.lineWidth = 1.5;
  g.strokeStyle = bright;
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.stroke();
}

/** Chevron pointing in the direction of `angle` (radians). Used for
 *  Flipper enemies. */
function strokeChevron(
  g: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  angle: number,
  size: number,
  bright: string,
  bloom: string
): void {
  // Chevron points outward (away from VP). Three vertices: nose + 2 tails.
  const nose = { x: cx + Math.cos(angle) * size * 0.5, y: cy + Math.sin(angle) * size * 0.5 };
  const tailA = {
    x: cx + Math.cos(angle + 2.4) * size * 0.5,
    y: cy + Math.sin(angle + 2.4) * size * 0.5,
  };
  const tailB = {
    x: cx + Math.cos(angle - 2.4) * size * 0.5,
    y: cy + Math.sin(angle - 2.4) * size * 0.5,
  };
  strokeLineBloom(g, tailA.x, tailA.y, nose.x, nose.y, bright, bloom);
  strokeLineBloom(g, tailB.x, tailB.y, nose.x, nose.y, bright, bloom);
  strokeLineBloom(g, tailA.x, tailA.y, tailB.x, tailB.y, bright, bloom);
}

function strokeDiamond(
  g: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  angle: number,
  size: number,
  bright: string,
  bloom: string
): void {
  const r = size * 0.5;
  const tips = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2].map((da) => ({
    x: cx + Math.cos(angle + da) * r,
    y: cy + Math.sin(angle + da) * r,
  }));
  for (let i = 0; i < 4; i++) {
    const a = tips[i];
    const b = tips[(i + 1) % 4];
    if (!a || !b) continue;
    strokeLineBloom(g, a.x, a.y, b.x, b.y, bright, bloom);
  }
}

function strokeRing(
  g: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  bright: string,
  bloom: string
): void {
  const r = size * 0.5;
  g.lineWidth = 3;
  g.strokeStyle = bloom;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 1.5;
  g.strokeStyle = bright;
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.stroke();
}

/** Player "claw" — a chevron with two arms reaching forward along the
 *  lane. Renders as if the defender is gripping the rim edge. */
function strokeClaw(
  g: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  angle: number,
  size: number,
  bright: string,
  bloom: string
): void {
  // Three arms: center + two side arms, all pointing inward (toward VP).
  // The center arm is longer than the side arms so the shape reads as a
  // "trident" — Tempest's defender homage.
  const inward = angle + Math.PI; // toward VP
  const base = { x: cx, y: cy };
  const centerTip = {
    x: cx + Math.cos(inward) * size * 0.8,
    y: cy + Math.sin(inward) * size * 0.8,
  };
  const sideA = {
    x: cx + Math.cos(inward + 0.6) * size * 0.55,
    y: cy + Math.sin(inward + 0.6) * size * 0.55,
  };
  const sideB = {
    x: cx + Math.cos(inward - 0.6) * size * 0.55,
    y: cy + Math.sin(inward - 0.6) * size * 0.55,
  };
  // Spreader bar across the rim.
  const left = {
    x: cx + Math.cos(angle + Math.PI / 2) * size * 0.5,
    y: cy + Math.sin(angle + Math.PI / 2) * size * 0.5,
  };
  const right = {
    x: cx + Math.cos(angle - Math.PI / 2) * size * 0.5,
    y: cy + Math.sin(angle - Math.PI / 2) * size * 0.5,
  };
  strokeLineBloom(g, left.x, left.y, right.x, right.y, bright, bloom);
  strokeLineBloom(g, base.x, base.y, centerTip.x, centerTip.y, bright, bloom);
  strokeLineBloom(g, left.x, left.y, sideA.x, sideA.y, bright, bloom);
  strokeLineBloom(g, right.x, right.y, sideB.x, sideB.y, bright, bloom);
}
